'use client';

import { Content } from '@prismicio/client';
import { usePathname, useSearchParams } from 'next/navigation';
import { Button, Container } from '@/components';
import { HeroList, Subtitle, Title } from '../../components';
import { HomePageHeroCardsContent } from './HomePageHeroCardsContent/HomePageHeroCardsContent';
import { Simplify } from '../../../../../prismicio-types';
import { useVisible } from '@/hooks';
import { getPageName } from '@/utils';
import { routes } from '@/constants';
import NextButton from '@/components/Button/NextButton';
import { MODAL_NAMES } from '@/constants/modals';
import useModal from '@/hooks/useModal';
import { ProspectData } from '@/types/salesforce';
import styles from './homepageHero.module.scss';

export const HomePageHero = ({
  data,
  prospect,
}: {
  data: Simplify<Content.HeroSliceDefaultPrimary>;
  prospect?: ProspectData;
}) => {
  const [isVisible, id] = useVisible();
  const path = usePathname();
  const pageName = getPageName(path);
  const searchParams = useSearchParams();
  const firstName = searchParams.get('firstName') || prospect?.First_Name__c;
  const { openModal } = useModal();

  const isLandingPage =
    pageName === routes.landingPage ||
    pageName === routes.preQualified ||
    pageName === routes.engineLanding;
  const landingTitleStyles = isLandingPage ? styles.landingTitle : undefined;
  const isPreQualifiedPage = pageName === routes.preQualified;
  const isEngineLanding = pageName === routes.engineLanding;

  return (
    <Container>
      <div
        className={`${styles.hero} ${isLandingPage ? styles.landingPage : ''}`}
        id={id}
      >
        <article className={styles.text_content}>
          <Title
            text={data.title}
            isVisible={isVisible}
            styles={landingTitleStyles}
            isPreQualifiedPage={isPreQualifiedPage}
            firstName={firstName}
            isEngineLanding={isEngineLanding}
          />
          {data.landing_page_second_title && (
            <Title
              text={data.landing_page_second_title}
              isVisible={isVisible}
              styles={landingTitleStyles}
            />
          )}
          <Subtitle subtitle={data.subtitle} isVisible={isVisible} />
          <HeroList list={data.advantages} isVisible={isVisible} />
          <div
            className={`${styles.button_wrapper} ${isVisible ? styles.isVisibleButton : ''}`}
          >
            {isPreQualifiedPage ? (
              <NextButton
                styles={styles.button}
                text={data.button_text as string}
                isArrow
                type="button"
                onClick={() => openModal({ name: MODAL_NAMES.preQualified })}
              />
            ) : isEngineLanding ? (
              <NextButton
                variation="primary"
                type="button"
                buttonType="button"
                styles={styles.button}
                text="Click here to continue with your offer"
                isArrow
                onClick={() =>
                  openModal({
                    name: MODAL_NAMES.engine,
                    data: { prospect: prospect || null },
                  })
                }
              />
            ) : (
              <Button
                variation="primary"
                text={data.button_text}
                link={data.button_link}
                styles={styles.button}
                isArrow
              />
            )}
          </div>
        </article>
        <article className={styles.cards_content}>
          <HomePageHeroCardsContent data={data} isVisible={isVisible} />
        </article>
      </div>
    </Container>
  );
};
